/* Pantry collection: filter/tag/sort updates via the Section Rendering API, load more, grid/list view, mobile drawer. */
if (!customElements.get('cp-collection')) {
  customElements.define('cp-collection', class CpCollection extends HTMLElement {
    disconnectedCallback() { if (this.onEsc) document.removeEventListener('keydown', this.onEsc); }

    connectedCallback() {
      this.sid = this.dataset.sectionId;
      document.documentElement.classList.add('cp-js');
      this.applyView(this.savedView());

      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-cp-form]') || e.target.matches('[data-cp-sort]')) {
          clearTimeout(this.t);
          this.t = setTimeout(() => this.fromForm(), e.target.type === 'number' ? 600 : 50);
        }
      });
      this.addEventListener('submit', (e) => {
        if (e.target.matches('[data-cp-form]')) { e.preventDefault(); this.fromForm(); }
      });
      this.addEventListener('click', (e) => {
        const nav = e.target.closest('a[data-cp-nav]');
        if (nav && !e.metaKey && !e.ctrlKey && !e.shiftKey) { e.preventDefault(); this.go(nav.href, true); return; }
        const more = e.target.closest('[data-cp-more]');
        if (more) { this.toggleMore(more); return; }
        const view = e.target.closest('[data-cp-view]');
        if (view) { this.applyView(view.dataset.cpView, true); return; }
        if (e.target.closest('[data-cp-load]')) { this.loadMore(e.target.closest('[data-cp-load]')); return; }
        if (e.target.closest('[data-cp-open]')) { this.openDrawer(); return; }
        if (e.target.closest('[data-cp-close]')) { this.closeDrawer(); }
      });
      this.onEsc = (e) => { if (e.key === 'Escape' && this.isOpen()) this.closeDrawer(); };
      document.addEventListener('keydown', this.onEsc);
      if (!window.__cpPop) {
        window.__cpPop = true;
        window.addEventListener('popstate', () => {
          const el = document.querySelector('cp-collection');
          if (el) el.go(location.href, false);
        });
      }
    }

    fromForm() {
      const form = this.querySelector('[data-cp-form]');
      const fd = new FormData(form);
      const sort = this.querySelector('[data-cp-sort]');
      if (sort) { if (sort.value && sort.value !== sort.dataset.default) fd.set("sort_by", sort.value); else fd.delete("sort_by"); }
      const params = new URLSearchParams();
      for (const [k, v] of fd) if (v !== '') params.append(k, v);
      const qs = params.toString();
      this.go(form.getAttribute('action') + (qs ? '?' + qs : ''), true);
    }

    async go(href, push) {
      const url = new URL(href, location.origin);
      const results = this.querySelector('.cp-results');
      results && results.classList.add('is-loading');
      const wasOpen = this.isOpen();
      const act = document.activeElement;
      const focusSel = act && this.contains(act) && act.name ? `[name="${CSS.escape(act.name)}"][value="${CSS.escape(act.value)}"]` : null;
      try {
        const fetchUrl = new URL(url);
        fetchUrl.searchParams.set('section_id', this.sid);
        const res = await fetch(fetchUrl.toString(), { headers: { Accept: 'text/html' } });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('cp-collection');
        if (!fresh) throw new Error('no section');
        const openAcc = {};
        this.querySelectorAll('details.cp-acc').forEach((d) => { openAcc[d.querySelector('summary')?.textContent.trim()] = d.open; });
        this.querySelector('.cp-layout').replaceWith(fresh.querySelector('.cp-layout'));
        this.querySelectorAll('details.cp-acc').forEach((d) => {
          const k = d.querySelector('summary')?.textContent.trim();
          if (k in openAcc && !d.querySelector(':checked, .is-on')) d.open = openAcc[k];
        });
        this.applyView(this.savedView());
        if (wasOpen) this.openDrawer(true);
        const back = focusSel && this.querySelector(focusSel);
        if (back) back.focus({ preventScroll: true });
        else if (wasOpen) this.querySelector('.cp-side__close')?.focus({ preventScroll: true });
        if (push) history.pushState({ cp: true }, '', url.pathname + url.search);
        const title = this.querySelector('.cp-title');
        if (!wasOpen && title && title.getBoundingClientRect().top < 0) title.scrollIntoView({ block: 'start' });
      } catch (err) {
        location.href = url.toString();
      }
    }

    toggleMore(btn) {
      const box = btn.parentElement;
      const open = btn.getAttribute('aria-expanded') !== 'true';
      box.querySelectorAll('.cp-more-item').forEach((li) => { if (!li.querySelector('.is-on,:checked')) li.hidden = !open; });
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? btn.dataset.less : btn.dataset.more;
    }

    savedView() { try { return localStorage.getItem('cp-view') || 'grid'; } catch (e) { return 'grid'; } }

    applyView(v, save) {
      const grid = this.querySelector('[data-cp-grid]');
      if (grid) grid.classList.toggle('cp-grid--list', v === 'list');
      this.querySelectorAll('[data-cp-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cpView === v)));
      if (save) { try { localStorage.setItem('cp-view', v); } catch (e) { /* storage unavailable */ } }
    }

    async loadMore(btn) {
      const next = btn.dataset.next;
      if (!next || btn.disabled) return;
      const label = btn.querySelector('span');
      const old = label.textContent;
      btn.disabled = true;
      label.textContent = btn.dataset.loading;
      try {
        const u = new URL(next, location.origin);
        u.searchParams.set('section_id', this.sid);
        const doc = new DOMParser().parseFromString(await (await fetch(u.toString())).text(), 'text/html');
        const items = doc.querySelectorAll('[data-cp-grid] > li');
        const grid = this.querySelector('[data-cp-grid]');
        const first = items[0];
        items.forEach((li) => grid.append(li));
        const nb = doc.querySelector('[data-cp-load]');
        const pages = doc.querySelector('.cp-pages');
        if (pages) this.querySelector('.cp-pages')?.replaceWith(pages);
        if (nb) { btn.dataset.next = nb.dataset.next; btn.disabled = false; label.textContent = old; } else { btn.remove(); }
        if (first) { const link = first.querySelector('.kt-card__title a'); link && link.focus({ preventScroll: true }); }
      } catch (e) {
        location.href = next;
      }
    }

    isOpen() { const s = this.querySelector('.cp-side'); return !!(s && s.classList.contains('is-open')); }

    openDrawer(silent) {
      const side = this.querySelector('.cp-side');
      if (!side || !window.matchMedia('(max-width: 989px)').matches) return;
      side.classList.add('is-open');
      side.setAttribute('role', 'dialog');
      side.setAttribute('aria-modal', 'true');
      this.querySelector('.cp-scrim').hidden = false;
      document.documentElement.classList.add('cp-lock');
      this.querySelectorAll('[data-cp-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      if (!silent) side.querySelector('.cp-side__close')?.focus();
      if (!this.trap) {
        this.trap = (e) => {
          if (e.key !== 'Tab' || !this.isOpen()) return;
          const f = [...this.querySelector('.cp-side').querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,summary')].filter((x) => x.offsetParent !== null);
          if (!f.length) return;
          if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
          else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        };
        this.addEventListener('keydown', this.trap);
      }
    }

    closeDrawer() {
      const side = this.querySelector('.cp-side');
      if (!side) return;
      side.classList.remove('is-open');
      side.removeAttribute('role');
      side.removeAttribute('aria-modal');
      this.querySelector('.cp-scrim').hidden = true;
      document.documentElement.classList.remove('cp-lock');
      this.querySelectorAll('[data-cp-open]').forEach((b) => { b.setAttribute('aria-expanded', 'false'); });
      this.querySelector('[data-cp-open]')?.focus();
    }
  });
}
